import { useEffect, useState, Fragment } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { getCurrentProject } from "../../../../actions/projectsActions";
import {
  getCurrentProjectBatch,
  getBatchCalculationsList,
  approveProjectPreDistribution,
  rejectProjectPreDistribution,
  markProjectPreDistributionReady,
  uploadPreDistributionManualReport,
  approveProjectBatchAll,
  rejectProjectBatchAll,
} from "../../../../actions/batchesActions";
import { resetBatchesState } from "../../../../slices/batchesSlice";
import { resetProjectsState } from "../../../../slices/projectsSlice";
import { showAlert } from "../../../../slices/alertsSlice";
import AppPagination from "../../../../components/AppPagination";
import PreDistributionReportsSection from "./PreDistributionReportsSection";
import ConfirmationDialog from "../../../../components/ConfirmationDialog";
import BatchApprovalProgress from "../../../../components/BatchApprovalProgress";
import ProportionalCalculationIndicator from "../../../../components/ProportionalCalculationIndicator";
import { currencyFormatter } from "../../../../utils/formatUtils";

const periodTypeLabels = {
  daily: "Diario",
  monthly: "Mensual",
  quarterly: "Trimestral",
  annual: "Anual",
};

const batchStatusColors = {
  pending: "warning",
  processing: "info",
  completed: "success",
  failed: "error",
  approved: "success",
  partially_approved: "info",
  rejected: "error",
  cancelled: "default",
};

const batchStatusLabels = {
  pending: "Pendiente",
  processing: "Procesando",
  completed: "Generado",
  failed: "Error",
  approved: "Aprobado",
  partially_approved: "Parcialmente Aprobado",
  rejected: "Rechazado",
  cancelled: "Cancelado",
};

const statusColors = {
  pending: "warning",
  calculated: "info",
  auto_generated: "info",
  ready_for_approval: "primary",
  approved: "success",
  rejected: "error",
};

const statusLabels = {
  pending: "Pendiente",
  calculated: "Calculado",
  auto_generated: "Listo para marcar",
  ready_for_approval: "Listo para aprobación",
  approved: "Aprobado",
  rejected: "Rechazado",
};

const BatchPreDistributionsPage = () => {
  const { id: projectId, batchId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const project = useSelector((state) => state.projects.current);
  const batch = useSelector((state) => state.batches.current);
  const calculations = useSelector((state) => state.batches.calculations);
  const pagination = useSelector(
    (state) => state.batches.calculationsPagination
  );

  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedPreDistribution, setSelectedPreDistribution] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [
    selectedPreDistributionForUpload,
    setSelectedPreDistributionForUpload,
  ] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState("");
  const [batchInfoExpanded, setBatchInfoExpanded] = useState(false);
  const [approveAllDialogOpen, setApproveAllDialogOpen] = useState(false);
  const [rejectAllDialogOpen, setRejectAllDialogOpen] = useState(false);
  const [markAllReadyDialogOpen, setMarkAllReadyDialogOpen] = useState(false);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const startItem =
    pagination.total > 0 ? (page - 1) * pagination.limit + 1 : 0;
  const endItem = Math.min(page * pagination.limit, pagination.total);
  const remaining = pagination.total - endItem;

  useEffect(() => {
    dispatch(getCurrentProject({ id: projectId }));
    dispatch(getCurrentProjectBatch({ projectId, batchId }));
    return () => {
      dispatch(resetBatchesState());
      dispatch(resetProjectsState());
    };
  }, [projectId, batchId]);

  useEffect(() => {
    dispatch(getBatchCalculationsList({ projectId, batchId, query: { page } }));
  }, [projectId, batchId, page]);

  const handleBack = () => {
    navigate(`/dashboard/proyectos/${projectId}/batches`);
  };

  const handleRefresh = () => {
    dispatch(getBatchCalculationsList({ projectId, batchId, query: { page } }));
  };

  const handleApproveClick = (preDistribution) => {
    setSelectedPreDistribution(preDistribution);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = () => {
    // confirmCommitAction("approve", selectedPreDistribution, () => {
    dispatch(
      approveProjectPreDistribution({
        preDistributionId: selectedPreDistribution._id,
        projectId,
        batchId,
        query: { page },
        notes: "Aprobación individual desde interfaz",
        callback: () => {
          setApproveDialogOpen(false);
          setSelectedPreDistribution(null);
          // Confirmar guardado exitoso
          dispatch(
            showAlert({
              message:
                "Pre-distribución aprobada y datos guardados exitosamente",
              type: "success",
            })
          );
        },
      })
    );
    // });
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setSelectedPreDistribution(null);
  };

  const handleRejectClick = (preDistribution) => {
    setSelectedPreDistribution(preDistribution);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = () => {
    // confirmCommitAction("reject", selectedPreDistribution, () => {
    dispatch(
      rejectProjectPreDistribution({
        preDistributionId: selectedPreDistribution._id,
        projectId,
        batchId,
        query: { page },
        reason: "Rechazo individual desde interfaz",
        callback: () => {
          setRejectDialogOpen(false);
          setSelectedPreDistribution(null);
          // Confirmar guardado exitoso
          dispatch(
            showAlert({
              message:
                "Pre-distribución rechazada y datos guardados exitosamente",
              type: "success",
            })
          );
        },
      })
    );
    // });
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setSelectedPreDistribution(null);
  };

  const handleMarkReadyClick = (preDistribution) => {
    // confirmCommitAction("markReady", preDistribution, () => {
    dispatch(
      markProjectPreDistributionReady({
        preDistributionId: preDistribution._id,
        projectId,
        batchId,
        query: { page },
        callback: () => {
          // Confirmar guardado exitoso
          dispatch(
            showAlert({
              message:
                "Pre-distribución marcada como lista y datos guardados exitosamente",
              type: "success",
            })
          );
        },
      })
    );
    // });
  };

  const handleUploadClick = (preDistribution) => {
    setSelectedPreDistributionForUpload(preDistribution);
    setUploadDialogOpen(true);
  };

  const handleUploadCancel = () => {
    setUploadDialogOpen(false);
    setSelectedPreDistributionForUpload(null);
    setSelectedFile(null);
    setUploadNotes("");
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      if (file.type !== "application/pdf") {
        dispatch(
          showAlert({
            message: "Solo se permiten archivos PDF",
            type: "error",
          })
        );
        return;
      }
      // Validar tamaño (máximo 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        dispatch(
          showAlert({
            message: "El archivo no debe exceder 10MB",
            type: "error",
          })
        );
        return;
      }
      setSelectedFile(file);
    }
  };

  // Función para validar datos antes de commit
  // const validateBeforeCommit = (action, data) => {
  //   const validations = {
  //     approve: () => {
  //       if (!data) return "No se ha seleccionado ninguna pre-distribución";
  //       if (data.status === "approved")
  //         return "Esta pre-distribución ya está aprobada";
  //       return null;
  //     },
  //     reject: () => {
  //       if (!data) return "No se ha seleccionado ninguna pre-distribución";
  //       if (data.status === "rejected")
  //         return "Esta pre-distribución ya está rechazada";
  //       return null;
  //     },
  //     upload: () => {
  //       if (!selectedFile) return "Por favor selecciona un archivo";
  //       if (!data) return "No se ha seleccionado ninguna pre-distribución";
  //       return null;
  //     },
  //     markReady: () => {
  //       if (!data) return "No se ha seleccionado ninguna pre-distribución";
  //       if (data.status === "approved")
  //         return "Esta pre-distribución ya está aprobada";
  //       if (data.status === "ready_for_approval")
  //         return "Esta pre-distribución ya está lista para aprobación";
  //       return null;
  //     },
  //   };

  //   return validations[action] ? validations[action]() : null;
  // };

  // Función mejorada para confirmar guardado con validaciones
  // const confirmCommitAction = (action, data, callback) => {
  //   const validationError = validateBeforeCommit(action, data);
  //   if (validationError) {
  //     dispatch(
  //       showAlert({
  //         message: validationError,
  //         type: "error",
  //       })
  //     );
  //     return false;
  //   }

  //   const confirmMessage = {
  //     approve:
  //       "¿Confirmas aprobar esta pre-distribución? Esta acción creará la distribución final.",
  //     reject:
  //       "¿Confirmas rechazar esta pre-distribución? Esta acción no se puede deshacer.",
  //     upload: "¿Confirmas subir este reporte manual?",
  //     markReady:
  //       "¿Confirmas marcar esta pre-distribución como lista para aprobación?",
  //     approveAll:
  //       "¿Confirmas aprobar todas las pre-distribuciones listas? Se crearán las distribuciones finales.",
  //     rejectAll:
  //       "¿Confirmas rechazar todas las pre-distribuciones del batch? Esta acción no se puede deshacer.",
  //   };

  //   if (window.confirm(confirmMessage[action])) {
  //     callback();
  //     return true;
  //   }
  //   return false;
  // };

  const handleUploadConfirm = () => {
    // if (
    //   !confirmCommitAction("upload", selectedPreDistributionForUpload, () => {
    dispatch(
      uploadPreDistributionManualReport({
        preDistributionId: selectedPreDistributionForUpload._id,
        projectId,
        batchId,
        query: { page },
        file: selectedFile,
        notes: uploadNotes,
        callback: () => {
          handleUploadCancel();
          dispatch(
            showAlert({
              message: "Datos guardados exitosamente",
              type: "success",
            })
          );
        },
      })
    );
    //   })
    // ) {
    //   return;
    // }
  };

  const handleApproveAllClick = () => {
    setApproveAllDialogOpen(true);
  };

  const handleApproveAllConfirm = () => {
    // confirmCommitAction("approveAll", batch, () => {
    dispatch(
      approveProjectBatchAll({
        projectId,
        batchId,
        notes: "Aprobación masiva desde interfaz de pre-distribuciones",
        callback: () => {
          handleRefresh();
          setApproveAllDialogOpen(false);
          // Confirmar guardado exitoso masivo
          dispatch(
            showAlert({
              message: `Todas las pre-distribuciones han sido aprobadas y los datos guardados exitosamente`,
              type: "success",
            })
          );
        },
      })
    );
    // });
  };

  const handleApproveAllCancel = () => {
    setApproveAllDialogOpen(false);
  };

  const handleRejectAllClick = () => {
    setRejectAllDialogOpen(true);
  };

  const handleRejectAllConfirm = () => {
    // confirmCommitAction("rejectAll", batch, () => {
    dispatch(
      rejectProjectBatchAll({
        projectId,
        batchId,
        reason: "Rechazo masivo desde interfaz de pre-distribuciones",
        callback: () => {
          handleRefresh();
          setRejectAllDialogOpen(false);
          // Confirmar guardado exitoso masivo
          dispatch(
            showAlert({
              message:
                "Todas las pre-distribuciones han sido rechazadas y los datos guardados exitosamente",
              type: "success",
            })
          );
        },
      })
    );
    // });
  };

  const handleRejectAllCancel = () => {
    setRejectAllDialogOpen(false);
  };

  const handleMarkAllReadyClick = () => {
    setMarkAllReadyDialogOpen(true);
  };

  const handleMarkAllReadyConfirm = () => {
    // Marcar todas las pre-distribuciones auto_generated como ready_for_approval
    const autoGeneratedCalcs = calculations.filter(
      (calc) => calc.status === "auto_generated"
    );

    if (autoGeneratedCalcs.length === 0) {
      dispatch(
        showAlert({
          message: "No hay pre-distribuciones para marcar como listas",
          type: "warning",
        })
      );
      setMarkAllReadyDialogOpen(false);
      return;
    }

    // Confirmar antes de proceder
    if (
      !window.confirm(
        `¿Confirmas marcar ${autoGeneratedCalcs.length} pre-distribuciones como listas para aprobación?`
      )
    ) {
      return;
    }

    // Marcar cada una y contar las completadas
    let completed = 0;
    const total = autoGeneratedCalcs.length;

    autoGeneratedCalcs.forEach((calc) => {
      dispatch(
        markProjectPreDistributionReady({
          preDistributionId: calc._id,
          projectId,
          batchId,
          query: { page },
          callback: () => {
            completed++;
            // Solo cerrar cuando todas hayan terminado
            if (completed === total) {
              setMarkAllReadyDialogOpen(false);
              handleRefresh();
              // Confirmar guardado exitoso masivo
              dispatch(
                showAlert({
                  message: `${total} pre-distribuciones marcadas como listas y datos guardados exitosamente`,
                  type: "success",
                })
              );
            }
          },
        })
      );
    });
  };

  const handleMarkAllReadyCancel = () => {
    setMarkAllReadyDialogOpen(false);
  };

  const nextPage = () => setPage(page + 1);
  const prevPage = () => setPage(Math.max(page - 1, 1));

  // Helper function to create calculation tooltips
  const getCalculationTooltip = (type, calc) => {
    console.log({ calc });
    const capital = calc.investmentRef?.capital || 0;
    const rendicimentoRecibido =
      calc.calculationInputs?.performanceReceived || 0;
    const porcentajeIDE = calc.calculationInputs?.participationIDE || 0;
    // const rendimientoAnual = calc.calculationMetadata?.annualReturn || 0;
    const periodFactor = calc.calculationInputs?.periodFactor || 0;
    const periodProRata =
      calc.calculationMetadata?.proRataInfo?.periodProRata || 0;
    const feePercentage = calc.calculationMetadata?.feePercentage || 0;
    const taxRetentionPercentage =
      calc.calculationMetadata?.taxRetentionPercentage || 0;
    const vehiculo = calc.investmentRef?.vehiculo || "";
    const isLLC = vehiculo.toUpperCase().includes("LLC");

    switch (type) {
      case "gross":
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              Rendimiento Bruto
            </Typography>
            <Typography variant="body2">
              Fórmula: Rendimiento recibido × Procentaje IDE × Factor Prorrateo
            </Typography>
            <Typography variant="body2">
              Datos: {currencyFormatter.format(rendicimentoRecibido)} ×{" "}
              {porcentajeIDE.toFixed(4)} ×{periodProRata.toFixed(3)}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="primary">
              Resultado:{" "}
              {currencyFormatter.format(calc.calculation?.grossAmount)}
            </Typography>

            {/* Información ProRata */}
            {calc.calculationMetadata.proRataInfo.usedProRata && (
              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #eee" }}>
                <Typography variant="body2" fontWeight="bold" color="info.main">
                  📊 Información ProRata
                </Typography>
                <Typography variant="caption" display="block">
                  Primera Distribución:{" "}
                  {calc.calculationMetadata.proRataInfo.isFirstDistribution
                    ? "Sí"
                    : "No"}
                </Typography>
                <Typography variant="caption" display="block">
                  Usa ProRata:{" "}
                  {calc.calculationMetadata.proRataInfo.usedProRata
                    ? "Sí"
                    : "No"}
                </Typography>
                <Typography variant="caption" display="block">
                  Días Efectivos:{" "}
                  {calc.calculationMetadata.proRataInfo.effectiveDays || "-"}
                </Typography>
                <Typography variant="caption" display="block">
                  Días Totales:{" "}
                  {calc.calculationMetadata.proRataInfo.totalPeriodDays || "-"}
                </Typography>
                <Typography variant="caption" display="block">
                  Factor Período:{" "}
                  {calc.calculationMetadata.proRataInfo.periodFactor
                    ? calc.calculationMetadata.proRataInfo.periodFactor.toFixed(
                        6
                      )
                    : "-"}
                </Typography>
                <Typography variant="caption" display="block">
                  Fecha Inversión:{" "}
                  {calc.calculationMetadata.proRataInfo.investmentDate
                    ? new Date(
                        calc.calculationMetadata.proRataInfo.investmentDate
                      ).toLocaleDateString()
                    : "-"}
                </Typography>
                <Typography variant="caption" display="block">
                  Período:{" "}
                  {calc.calculationMetadata.proRataInfo.periodStartDate
                    ? new Date(
                        calc.calculationMetadata.proRataInfo.periodStartDate
                      ).toLocaleDateString()
                    : "-"}{" "}
                  -{" "}
                  {calc.calculationMetadata.proRataInfo.periodEndDate
                    ? new Date(
                        calc.calculationMetadata.proRataInfo.periodEndDate
                      ).toLocaleDateString()
                    : "-"}
                </Typography>
                {calc.calculationMetadata.proRataInfo.proRataReason && (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    Razón: {calc.calculationMetadata.proRataInfo.proRataReason}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );

      case "fees":
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              Fee Management
            </Typography>
            <Typography variant="body2">
              Fórmula: Capital × (Fee%) × Factor Período x (ProRata)
            </Typography>
            <Typography variant="body2">
              Datos: ${capital.toLocaleString()} × {feePercentage}% ×{" "}
              {periodFactor.toFixed(3)}{" "}
              {`(ProRata: ${periodProRata.toFixed(3)})`}
            </Typography>
            <Typography variant="body2" color="primary">
              Resultado: {currencyFormatter.format(calc.calculation?.fees)}
            </Typography>
          </Box>
        );

      case "taxRetention":
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              Retención de Impuestos
            </Typography>
            <Typography variant="body2">
              Fórmula: (Rendimiento Bruto - Fee) × (Retención%)
            </Typography>
            {isLLC ? (
              <Typography variant="body2" color="info.main">
                Vehículo LLC: No aplica retención (= 0)
              </Typography>
            ) : (
              <Typography variant="body2">
                Datos: (
                {currencyFormatter.format(calc.calculation?.grossAmount)} -{" "}
                {currencyFormatter.format(calc.calculation?.fees)}) ×{" "}
                {taxRetentionPercentage}%
              </Typography>
            )}
            <Typography variant="body2" color="primary">
              Resultado:{" "}
              {currencyFormatter.format(calc.calculation?.taxRetention)}
            </Typography>
          </Box>
        );

      case "net":
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              Rendimiento Neto
            </Typography>
            <Typography variant="body2">
              Fórmula: Rendimiento Bruto - Fee - Retención
            </Typography>
            <Typography variant="body2">
              Datos: {currencyFormatter.format(calc.calculation?.grossAmount)} -{" "}
              {currencyFormatter.format(calc.calculation?.fees)} -{" "}
              {currencyFormatter.format(calc.calculation?.taxRetention)}
            </Typography>
            <Typography variant="body2" color="primary">
              Resultado: {currencyFormatter.format(calc.calculation?.netAmount)}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            Pre-distribuciones del Batch
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Proyecto: {project?.name || projectId}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ mr: 1 }}
        >
          Actualizar
        </Button>
      </Box>

      {batch && (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableBody>
              <TableRow hover>
                <TableCell width={40}>
                  <IconButton
                    size="small"
                    onClick={() => setBatchInfoExpanded(!batchInfoExpanded)}
                  >
                    {batchInfoExpanded ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {batch.period?.label ||
                      periodTypeLabels[batch.period?.type] ||
                      "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Información del Batch
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={batchStatusLabels[batch.status] || batch.status}
                    color={batchStatusColors[batch.status] || "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    Inversiones
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {batch.summary?.totalInvestments || 0}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    Asociados
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {batch.summary?.uniqueAssociates || 0}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    Monto Bruto
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {currencyFormatter.format(batch.summary?.totalGrossAmount)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    Fees
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {currencyFormatter.format(batch.summary?.totalFees)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    Retención
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {currencyFormatter.format(
                      batch.summary?.totalTaxRetention || 0
                    )}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" color="text.secondary">
                    Monto Neto
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {currencyFormatter.format(batch.summary?.totalNetAmount)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 0, bgcolor: "grey.50" }}>
                  <Collapse in={batchInfoExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ py: 2, px: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ mb: 2 }}
                      >
                        Acciones generales para el Batch
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3} md={3}>
                          <BatchApprovalProgress
                            summary={batch.summary}
                            size="small"
                            showPercentage={true}
                            showProgressBar={true}
                          />
                        </Grid>
                        <Grid item xs={12} sm={9} md={9}>
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              gap: 2,
                              flexWrap: "wrap",
                              alignItems: "center",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={handleApproveAllClick}
                              disabled={!batch.summary?.readyForApprovalCount}
                            >
                              Aprobar todas
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<PlaylistAddCheckIcon />}
                              onClick={handleMarkAllReadyClick}
                              disabled={
                                !calculations.some(
                                  (calc) => calc.status === "auto_generated"
                                )
                              }
                            >
                              Marcar como listo
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={handleRejectAllClick}
                            >
                              Rechazar restantes
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          px: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Mostrando {startItem} - {endItem} de {pagination.total} registros
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Página {page} de {pagination.pages || 1} | Faltan: {remaining}{" "}
          registros
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Asociado</TableCell>
              <TableCell>Inversión</TableCell>
              <TableCell>Vehículo</TableCell>
              <TableCell align="right">Capital</TableCell>
              <TableCell align="center">Monto Bruto</TableCell>
              <TableCell align="center">Fee Management</TableCell>
              <TableCell align="center">Retención</TableCell>
              <TableCell align="center">Monto Neto</TableCell>
              <TableCell align="center">ProRata</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calculations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No hay pre-distribuciones
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              calculations.map((calc, index) => (
                <Fragment key={`${calc._id}-${index}`}>
                  <TableRow hover>
                    <TableCell>
                      {calc.accumulated && (
                        <IconButton
                          size="small"
                          onClick={() => toggleRow(calc._id)}
                        >
                          {expandedRows[calc._id] ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      {calc.associateRef?._id && calc.investmentRef?._id ? (
                        <Link
                          to={`/dashboard/asociados/${
                            calc.associateRef?._id ?? ""
                          }/inversion/${calc.investmentRef._id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none" }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            color="primary"
                            sx={{
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {calc.associate?.name || calc.associateRef || "-"}
                          </Typography>
                        </Link>
                      ) : (
                        <Typography variant="body2" fontWeight="medium">
                          {calc.associate?.name || calc.associateRef || "-"}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {calc.associate?.email || ""}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <ProportionalCalculationIndicator
                          isProportional={calc.isProportional}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {calc.investment?.alias ||
                          calc?.investmentRef?.investor ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {calc.investment?.alias ||
                          calc?.investmentRef?.vehiculo ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {currencyFormatter.format(calc.investmentRef?.capital)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={getCalculationTooltip("gross", calc)}
                        placement="bottom-end"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "white",
                              color: "black",
                              border: "1px solid #ccc",
                              boxShadow: 2,
                              "& .MuiTooltip-arrow": {
                                color: "white",
                                "&:before": {
                                  border: "1px solid #ccc",
                                },
                              },
                            },
                          },
                        }}
                      >
                        <Box sx={{ cursor: "help" }}>
                          {currencyFormatter.format(
                            calc.calculation?.grossAmount
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={getCalculationTooltip("fees", calc)}
                        placement="bottom-end"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "white",
                              color: "black",
                              border: "1px solid #ccc",
                              boxShadow: 2,
                              "& .MuiTooltip-arrow": {
                                color: "white",
                                "&:before": {
                                  border: "1px solid #ccc",
                                },
                              },
                            },
                          },
                        }}
                      >
                        <Box sx={{ cursor: "help" }}>
                          {currencyFormatter.format(calc.calculation?.fees)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={getCalculationTooltip("taxRetention", calc)}
                        placement="bottom-end"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "white",
                              color: "black",
                              border: "1px solid #ccc",
                              boxShadow: 2,
                              "& .MuiTooltip-arrow": {
                                color: "white",
                                "&:before": {
                                  border: "1px solid #ccc",
                                },
                              },
                            },
                          },
                        }}
                      >
                        <Box sx={{ cursor: "help" }}>
                          {currencyFormatter.format(
                            calc.calculation?.taxRetention
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={getCalculationTooltip("net", calc)}
                        placement="bottom-end"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "white",
                              color: "black",
                              border: "1px solid #ccc",
                              boxShadow: 2,
                              "& .MuiTooltip-arrow": {
                                color: "white",
                                "&:before": {
                                  border: "1px solid #ccc",
                                },
                              },
                            },
                          },
                        }}
                      >
                        <Box sx={{ cursor: "help" }}>
                          <Typography fontWeight="medium">
                            {currencyFormatter.format(
                              calc.calculation?.netAmount
                            )}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        color={
                          calc.calculationMetadata.proRataInfo?.usedProRata
                            ? "primary"
                            : "text.secondary"
                        }
                      >
                        {calc.calculationMetadata.proRataInfo?.usedProRata
                          ? "Sí"
                          : "No"}
                      </Typography>
                      {calc.calculationMetadata.proRataInfo
                        ?.isFirstDistribution && (
                        <Typography variant="caption" color="info.main">
                          Primera
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={statusLabels[calc.status] || calc.status}
                        color={statusColors[calc.status] || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {calc.status === "ready_for_approval" && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApproveClick(calc)}
                            title="Aprobar"
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectClick(calc)}
                            title="Rechazar"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      {calc.status === "auto_generated" && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleMarkReadyClick(calc)}
                            title="Marcar como lista para aprobación"
                          >
                            <PlaylistAddCheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleUploadClick(calc)}
                            title="Subir documento manual"
                          >
                            <UploadFileIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectClick(calc)}
                            title="Rechazar"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      {calc.status !== "ready_for_approval" &&
                        calc.status !== "auto_generated" && (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                    </TableCell>
                  </TableRow>
                  {calc.accumulated && (
                    <TableRow key={`${calc._id}-collapse`}>
                      <TableCell
                        colSpan={11}
                        sx={{ py: 0, bgcolor: "grey.50" }}
                      >
                        <Collapse
                          in={expandedRows[calc._id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 2 }}>
                            <Grid container spacing={4}>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  sx={{ mb: 1 }}
                                >
                                  Acumulado Anterior
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={6} sm={3}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Distribución
                                    </Typography>
                                    <Typography variant="body2">
                                      {currencyFormatter.format(
                                        calc.accumulated.accumulatedDistribution
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Fees
                                    </Typography>
                                    <Typography variant="body2">
                                      {currencyFormatter.format(
                                        calc.accumulated.accumulatedFees
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Retención
                                    </Typography>
                                    <Typography variant="body2">
                                      {currencyFormatter.format(
                                        calc.accumulated.accumulatedRetention
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Neto
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      {currencyFormatter.format(
                                        calc.accumulated.netAccumulated
                                      )}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  sx={{ mb: 1 }}
                                >
                                  Nuevo Acumulado
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={6} sm={3}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Distribución
                                    </Typography>
                                    <Typography variant="body2">
                                      {currencyFormatter.format(
                                        calc.accumulated
                                          .newAccumulatedDistribution
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Fees
                                    </Typography>
                                    <Typography variant="body2">
                                      {currencyFormatter.format(
                                        calc.accumulated.newAccumulatedFees
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Retención
                                    </Typography>
                                    <Typography variant="body2">
                                      {currencyFormatter.format(
                                        calc.accumulated.newAccumulatedRetention
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={3}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Neto
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                      color="primary"
                                    >
                                      {currencyFormatter.format(
                                        calc.accumulated.newNetAccumulated
                                      )}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>

                            {/* {calc.calculationMetadata.proRataInfo && (
                              <Grid container spacing={4} sx={{ mt: 2 }}>
                                <Grid item xs={12}>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                    sx={{ mb: 1 }}
                                  >
                                    Información ProRata
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Primera Distribución
                                      </Typography>
                                      <Typography variant="body2">
                                        {calc.calculationMetadata.proRataInfo
                                          .isFirstDistribution
                                          ? "Sí"
                                          : "No"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Usa ProRata
                                      </Typography>
                                      <Typography variant="body2">
                                        {calc.calculationMetadata.proRataInfo
                                          .usedProRata
                                          ? "Sí"
                                          : "No"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Días Efectivos
                                      </Typography>
                                      <Typography variant="body2">
                                        {calc.calculationMetadata.proRataInfo
                                          .effectiveDays || "-"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Días Totales
                                      </Typography>
                                      <Typography variant="body2">
                                        {calc.calculationMetadata.proRataInfo
                                          .totalPeriodDays || "-"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Factor Período
                                      </Typography>
                                      <Typography variant="body2">
                                        {calc.calculationMetadata.proRataInfo
                                          .periodFactor
                                          ? calc.calculationMetadata.proRataInfo.periodFactor.toFixed(
                                              6
                                            )
                                          : "-"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Fecha Inversión
                                      </Typography>
                                      <Typography variant="body2">
                                        {calc.calculationMetadata.proRataInfo
                                          .investmentDate
                                          ? new Date(
                                              calc.calculationMetadata.proRataInfo.investmentDate
                                            ).toLocaleDateString()
                                          : "-"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Inicio Período
                                      </Typography>
                                      <Typography variant="body2">
                                        {calc.calculationMetadata.proRataInfo
                                          .periodStartDate
                                          ? new Date(
                                              calc.calculationMetadata.proRataInfo.periodStartDate
                                            ).toLocaleDateString()
                                          : "-"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Fin Período
                                      </Typography>
                                      <Typography variant="body2">
                                        {calc.calculationMetadata.proRataInfo
                                          .periodEndDate
                                          ? new Date(
                                              calc.calculationMetadata.proRataInfo.periodEndDate
                                            ).toLocaleDateString()
                                          : "-"}
                                      </Typography>
                                    </Grid>
                                    {calc.calculationMetadata.proRataInfo
                                      .proRataReason && (
                                      <Grid item xs={12}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Razón ProRata
                                        </Typography>
                                        <Typography variant="body2">
                                          {
                                            calc.calculationMetadata.proRataInfo
                                              .proRataReason
                                          }
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>
                            )} */}

                            <PreDistributionReportsSection
                              preDistributionId={calc._id}
                              projectId={projectId}
                              batchId={batchId}
                              currentPage={page}
                              reportsAuto={calc.reportsAuto}
                              reportsManual={calc.reportsManual}
                              reportMerged={calc.reportMerged}
                            />

                            <Box display={"flex"} justifyContent={"end"} mt={2}>
                              <Typography
                                variant="caption"
                                align="right"
                                textAlign={"right"}
                                color={"text.secondary"}
                              >
                                ID: {calc._id}
                              </Typography>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1,
          px: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Mostrando {startItem} - {endItem} de {pagination.total} registros
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Página {page} de {pagination.pages || 1} | Faltan: {remaining}{" "}
          registros
        </Typography>
      </Box>

      <AppPagination page={page} prev={prevPage} next={nextPage} />

      <ConfirmationDialog
        open={approveDialogOpen}
        onClose={handleApproveCancel}
        onConfirm={handleApproveConfirm}
        title="Aprobar Pre-Distribution"
        message={`¿Estás seguro de que deseas aprobar esta pre-distribution para ${
          selectedPreDistribution?.associate?.name || "este asociado"
        }? Esta acción creará la distribución y el reporte final`}
        confirmText="Aprobar"
        cancelText="Cancelar"
      />

      <ConfirmationDialog
        open={rejectDialogOpen}
        onClose={handleRejectCancel}
        onConfirm={handleRejectConfirm}
        title="Rechazar Pre-Distribution"
        message={`¿Estás seguro de que deseas rechazar esta pre-distribution para ${
          selectedPreDistribution?.associate?.name || "este asociado"
        }? Esta acción no se puede deshacer.`}
        confirmText="Rechazar"
        cancelText="Cancelar"
      />

      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Subir Documento Manual</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Asociado:{" "}
              {selectedPreDistributionForUpload?.associate?.name || "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Inversión:{" "}
              {selectedPreDistributionForUpload?.investment?.alias || "-"}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <input
                accept="application/pdf"
                style={{ display: "none" }}
                id="upload-manual-report"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="upload-manual-report">
                <Button variant="outlined" component="span" fullWidth>
                  {selectedFile ? selectedFile.name : "Seleccionar archivo PDF"}
                </Button>
              </label>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Solo archivos PDF (máximo 10MB)
              </Typography>
            </Box>
            <TextField
              label="Notas (opcional)"
              multiline
              rows={3}
              fullWidth
              value={uploadNotes}
              onChange={(e) => setUploadNotes(e.target.value)}
              placeholder="Agregar notas sobre el documento..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadCancel}>Cancelar</Button>
          <Button
            onClick={handleUploadConfirm}
            variant="contained"
            disabled={!selectedFile}
          >
            Subir
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={approveAllDialogOpen}
        onClose={handleApproveAllCancel}
        onConfirm={handleApproveAllConfirm}
        title="Aprobar Todas las Pre-Distribuciones Listas"
        message={`¿Estás seguro de que deseas aprobar todas las pre-distribuciones que están listas para aprobación? Se aprobarán ${
          batch?.summary?.readyForApprovalCount || 0
        } pre-distribuciones. Esta acción creará las distribuciones y reportes finales.`}
        confirmText="Aprobar Todas"
        cancelText="Cancelar"
      />

      <ConfirmationDialog
        open={rejectAllDialogOpen}
        onClose={handleRejectAllCancel}
        onConfirm={handleRejectAllConfirm}
        title="Rechazar Todas las Pre-Distribuciones Restantes"
        message="¿Estás seguro de que deseas rechazar todas las pre-distribuciones restantes de este batch? Esta acción no se puede deshacer fácilmente."
        confirmText="Rechazar Todas"
        cancelText="Cancelar"
      />

      <ConfirmationDialog
        open={markAllReadyDialogOpen}
        onClose={handleMarkAllReadyCancel}
        onConfirm={handleMarkAllReadyConfirm}
        title="Marcar Todas como Listas para Aprobación"
        message={`¿Estás seguro de que deseas marcar todas las pre-distribuciones auto-generadas como listas para aprobación? Se marcarán ${
          calculations.filter((calc) => calc.status === "auto_generated").length
        } pre-distribuciones.`}
        confirmText="Marcar Todas"
        cancelText="Cancelar"
      />
    </Box>
  );
};

export default BatchPreDistributionsPage;
